import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ShieldCheck, Plus, Trash2, Save, Building2, RotateCcw, Search, UserCheck, AlertTriangle } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";

const MODULE_LABEL = {
  estimate: "Estimate",
  sales_invoice: "Sales Invoice",
  purchase_invoice: "Purchase Invoice",
  purchase_order: "Purchase Order",
  expense_voucher: "Expense Voucher",
  payment_receipt: "Payment Receipt",
  store_rent: "Store Rent Payment",
  salary_payment: "Salary Payment",
  general_payment: "General Payment",
  bill_payment_voucher: "Bill Payment Voucher",
  vendor_advance: "Vendor Advance",
  payment: "Payment",
  cash_bank: "Cash / Bank",
  issue_voucher: "Issue Voucher",
  return_voucher: "Return Voucher",
  stock_transfer: "Stock Transfer",
  sales_credit_note: "Sales Credit Note",
  sales_debit_note: "Sales Debit Note",
  jms_completion: "JMS Completion",
  asset_scrap: "Asset Scrap",
  send_to_repair: "Send to Repair",
  asset_mark_ready: "Mark Ready (Repair Done)",
  // Sprint 2 — Project Budget bundle (8-row batch approved together).
  project_budget: "Project Budget (Sprint 2)",
  // Sprint 2.2 — Material Transfer Voucher (project → project stock move).
  material_transfer_voucher: "Material Transfer Voucher (MTV)",
  // Sprint 4.11.17 — Vendor Master creation / edit gated by approval before
  // the vendor becomes usable in transactions (PI/PO/payments).
  vendor_master: "Vendor Master",
  // Sprint 2026-Q1e — Warehouse Transfer now routes through the Approval
  // Matrix (previously bypassed via a direct-approve endpoint). Adding
  // the label here lets admins configure per-branch approvers for
  // within-branch inter-warehouse stock/asset movements.
  warehouse_transfer: "Warehouse Transfer",
  // Jul-2026 — Batch Scrap Sale voucher (warehouse clearing on final approval).
  scrap_sale: "Scrap Sale Voucher",
  // Jun-2026 (user-reported) — Journal Voucher categories. OI was missing
  // entirely (no label + not in backend APPROVAL_MODULES), so admins could
  // not tag approvers for Other Income JVs.
  journal_voucher_eb: "Journal Voucher · Employee Benefit",
  journal_voucher_fc: "Journal Voucher · Finance Cost",
  journal_voucher_oe: "Journal Voucher · Other Expenses",
  journal_voucher_oi: "Journal Voucher · Other Income",
  // Aug-2026 — pre-deployment audit: these two were registered in
  // APPROVAL_MODULES but missing here (save/remove toasts showed "undefined").
  stock_adjustment_voucher: "Stock Adjustment Voucher",
  bank_recon_exception: "Bank Recon Exception",
  // Jun-2026 (user request) — post-approval PO cancellation approval chain.
  po_cancellation: "PO Cancellation",
  // Aug-2026 — Phase 1: Project Scope & BOQ version approval (maker/checker;
  // final approval installs the version as the project's single baseline).
  project_boq: "Project Scope & BOQ",
  // Aug-2026 — Phase 2A: Project Purchase Requisition (fail-closed like BOQ).
  project_purchase_requisition: "Project Purchase Requisition",
  // Jun-2026 — Phase 3C: Project GRN (fail-closed; posting on final approval).
  project_grn: "Project GRN",
  labour_payment: "Labour Payment Request",
};

export default function ApprovalMatrixPage() {
  const [matrices, setMatrices] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  // Jun-2026 — Role-Based Approvers: role list + live eligible-member preview.
  const [roles, setRoles] = useState([]);
  const [eligibility, setEligibility] = useState({});   // { "roleId|branchId": {eligible_count, members, ...} }
  const inflightRef = useRef(new Set());
  const [activeBranch, setActiveBranch] = useState("");   // "" = default (applies to all)
  const [activeModule, setActiveModule] = useState("estimate");
  // Sprint 4.11.17 — per-level user search. Filters the checkbox grid so
  // admins can find an approver fast in a tenant with hundreds of users.
  const [userSearch, setUserSearch] = useState({});       // { [levelIdx]: "search text" }
  // Aug-2026 (user request) — module search for the left rail.
  const [moduleSearch, setModuleSearch] = useState("");

  const load = (bid = activeBranch) => {
    const url = bid ? `/approval-matrix?branch_id=${encodeURIComponent(bid)}` : "/approval-matrix";
    Promise.all([api.get(url), api.get("/users"), api.get("/branches"), api.get("/roles")])
      .then(([m, u, b, r]) => {
        setMatrices(m.data || []);
        setUsers(u.data || []);
        setBranches(b.data || []);
        setRoles(r.data || []);
      });
  };
  useEffect(() => { load(activeBranch); /* eslint-disable-next-line */ }, [activeBranch]);

  async function fetchEligibility(roleId, bid = activeBranch) {
    if (!roleId) return;
    const key = `${roleId}|${bid || ""}`;
    if (eligibility[key] || inflightRef.current.has(key)) return;
    inflightRef.current.add(key);
    try {
      const r = await api.get(`/approval-matrix-role-eligibility?role_id=${encodeURIComponent(roleId)}&branch_id=${encodeURIComponent(bid || "")}`);
      setEligibility(p => ({ ...p, [key]: r.data }));
    } catch {
      setEligibility(p => ({ ...p, [key]: { eligible_count: 0, members: [], error: true } }));
    } finally {
      inflightRef.current.delete(key);
    }
  }

  // Prefetch eligibility for every role row already saved in any matrix.
  useEffect(() => {
    (matrices || []).forEach(m => (m.levels || []).forEach(lv => {
      if ((lv.approver_type || "user") === "role" && lv.role_id) fetchEligibility(lv.role_id);
    }));
    // eslint-disable-next-line
  }, [matrices, activeBranch]);

  const matrix = matrices.find(m => m.module === activeModule) || { module: activeModule, levels: [], admin_is_final: true, branch_id: activeBranch, _is_override: false };
  const isOverride = !!matrix._is_override;

  function updateLocal(updater) {
    // Fall back to appending the synthesized stub when the module has no
    // matrix entry yet (otherwise Add Level / toggles silently no-op).
    const exists = matrices.some(m => m.module === activeModule);
    const next = exists
      ? matrices.map(m => m.module === activeModule ? updater(m) : m)
      : [...matrices, updater(matrix)];
    setMatrices(next);
  }

  function addLevel() {
    updateLocal(m => {
      const nextLvl = (m.levels?.length || 0) + 1;
      return { ...m, levels: [...(m.levels || []), { level: nextLvl, user_ids: [] }] };
    });
  }
  function removeLevel(idx) {
    updateLocal(m => ({
      ...m,
      levels: (m.levels || []).filter((_, i) => i !== idx).map((lv, i) => ({ ...lv, level: i + 1 })),
    }));
  }
  function toggleUserAtLevel(idx, userId) {
    updateLocal(m => {
      const lvls = [...(m.levels || [])];
      const cur = lvls[idx];
      const exists = (cur.user_ids || []).includes(userId);
      lvls[idx] = { ...cur, user_ids: exists ? cur.user_ids.filter(u => u !== userId) : [...(cur.user_ids || []), userId] };
      return { ...m, levels: lvls };
    });
  }
  function toggleAdminFinal() {
    updateLocal(m => ({ ...m, admin_is_final: !m.admin_is_final }));
  }
  // Jun-2026 — Role-Based Approvers: type switch clears the other selector
  // (a level is EITHER user-based OR role-based, never both).
  function setLevelType(idx, type) {
    updateLocal(m => {
      const lvls = [...(m.levels || [])];
      const cur = lvls[idx];
      lvls[idx] = type === "role"
        ? { level: cur.level, approver_type: "role", role_id: "", user_ids: [] }
        : { level: cur.level, user_ids: [] };
      return { ...m, levels: lvls };
    });
  }
  function setLevelRole(idx, roleId) {
    updateLocal(m => {
      const lvls = [...(m.levels || [])];
      lvls[idx] = { ...lvls[idx], approver_type: "role", role_id: roleId, user_ids: [] };
      return { ...m, levels: lvls };
    });
    if (roleId) fetchEligibility(roleId);
  }

  async function save() {
    try {
      await api.put(`/approval-matrix/${activeModule}`, {
        branch_id: activeBranch || "",
        levels: matrix.levels || [],
        admin_is_final: matrix.admin_is_final !== false,
      });
      const scope = activeBranch
        ? `${branches.find(b => b.id === activeBranch)?.branch_code || "branch"} override`
        : "default (all branches)";
      toast.success(`${MODULE_LABEL[activeModule]} matrix saved — ${scope}`);
      load(activeBranch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }

  async function revertToDefault() {
    if (!activeBranch) return;
    const branchName = branches.find(b => b.id === activeBranch)?.branch_name || "this branch";
    if (!window.confirm(`Remove the ${MODULE_LABEL[activeModule]} override for "${branchName}"? This branch will fall back to the Default matrix.`)) return;
    try {
      await api.delete(`/approval-matrix/${activeModule}?branch_id=${encodeURIComponent(activeBranch)}`);
      toast.success(`Override removed — ${branchName} now uses the Default matrix`);
      load(activeBranch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    }
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-6xl">
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <ShieldCheck size={24} className="text-[#F97316] shrink-0 md:w-7 md:h-7" />
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Approval Matrix</h1>
          <div className="text-xs md:text-sm text-slate-500">Configure per-module approvers (L1 = lowest, top = highest)</div>
        </div>
      </div>

      {/* Branch scope selector */}
      <div className="border border-slate-200 bg-white p-3 sm:p-4 mb-3 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <Building2 size={16} className="text-[#F97316]" />
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700">Branch Scope</div>
        </div>
        <select
          data-testid="approval-branch-picker"
          value={activeBranch}
          onChange={e => setActiveBranch(e.target.value)}
          className="text-xs font-semibold border border-slate-300 px-3 py-2 bg-white text-slate-800 focus:border-[#F97316] focus:outline-none w-full md:w-auto md:min-w-[280px]"
        >
          <option value="">Default — applies to ALL branches (fallback)</option>
          {branches.map(b => (
            <option key={b.id} value={b.id}>
              {b.branch_code ? `${b.branch_code} · ` : ""}{b.branch_name}
            </option>
          ))}
        </select>
        {activeBranch ? (
          isOverride ? (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F97316] text-white px-2 py-1" data-testid="branch-override-badge">
              Branch override active
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-600 px-2 py-1" data-testid="branch-using-default-badge">
              Using default — save to create override
            </span>
          )
        ) : (
          <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2 py-1">
            Default matrix
          </span>
        )}
        <div className="text-[11px] text-slate-500 md:ml-auto leading-snug">
          {activeBranch
            ? "Edits here create a branch-specific override. Other branches keep using the Default."
            : "Edits here apply to every branch that doesn't have its own override."}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:gap-6 items-start">
        <div className="w-full lg:w-56 lg:shrink-0 border border-slate-200 bg-white max-h-64 lg:max-h-[calc(100vh-160px)] overflow-y-auto lg:sticky lg:top-4">
          <div className="relative p-2 border-b border-slate-200 sticky top-0 bg-white z-10">
            <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              data-testid="module-search"
              value={moduleSearch}
              onChange={e => setModuleSearch(e.target.value)}
              placeholder="Search modules…"
              className="w-full pl-6 pr-6 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none"
            />
            {moduleSearch && (
              <button
                data-testid="module-search-clear"
                onClick={() => setModuleSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs leading-none">
                ×
              </button>
            )}
          </div>
          {/* Union of (modules that already have a matrix) + (every module
              the frontend knows about) so new modules added in a backend
              release immediately appear in the left rail for the admin
              to configure, even before any matrix doc exists for them. */}
          {(() => {
            const allMods = Array.from(new Set([
              ...matrices.map(m => m.module),
              ...Object.keys(MODULE_LABEL),
            ]));
            const mq = moduleSearch.trim().toLowerCase();
            const shown = mq
              ? allMods.filter(mod =>
                  (MODULE_LABEL[mod] || mod.replace(/_/g, " ")).toLowerCase().includes(mq)
                  || mod.toLowerCase().includes(mq))
              : allMods;
            if (shown.length === 0) return (
              <div className="px-4 py-3 text-xs text-slate-500 italic" data-testid="module-search-empty">
                No modules match &ldquo;{moduleSearch}&rdquo;
              </div>
            );
            return shown.map(mod => (
            <button
              key={mod}
              data-testid={`module-tab-${mod}`}
              onClick={() => setActiveModule(mod)}
              className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-100 ${activeModule === mod ? "bg-[#F97316] text-white font-bold" : "text-slate-700 hover:bg-slate-50"}`}>
              {MODULE_LABEL[mod] || mod.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
              {matrices.find(m => m.module === mod)?.levels?.length > 0 && (
                <span className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${activeModule === mod ? "bg-white/20" : "bg-emerald-100 text-emerald-800"}`}>
                  {matrices.find(m => m.module === mod).levels.length} lvls
                </span>
              )}
            </button>
          ));})()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="border border-slate-200 bg-white p-4 sm:p-5 mb-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                data-testid="admin-is-final"
                checked={matrix.admin_is_final !== false}
                onChange={toggleAdminFinal}
                className="accent-[#F97316]"
              />
              <span className="font-semibold">Admin can always approve as the final authority</span>
            </label>
            <div className="text-[11px] text-slate-500 mt-1 ml-6">
              When enabled, any user with role &ldquo;Admin&rdquo; can approve any pending request regardless of level.
            </div>
          </div>

          {(matrix.levels || []).length === 0 && (
            <div className="border border-dashed border-slate-300 bg-slate-50 p-8 text-center mb-3">
              <div className="text-sm text-slate-500">No levels configured for {MODULE_LABEL[activeModule]}.</div>
              <div className="text-xs text-slate-400 mt-1">Click &ldquo;Add Level&rdquo; to start.</div>
            </div>
          )}

          <div className="space-y-2 mb-4">
            {(matrix.levels || []).map((lv, idx) => {
              const isRole = (lv.approver_type || "user") === "role";
              const q = (userSearch[idx] || "").trim().toLowerCase();
              const filteredUsers = q
                ? users.filter(u => {
                    const hay = `${u.name || ""} ${u.email || ""} ${u.role || ""}`.toLowerCase();
                    return hay.includes(q);
                  })
                : users;
              const selectedCount = (lv.user_ids || []).length;
              const roleName = (roles.find(r => r.id === lv.role_id) || {}).name || lv.role_name_snapshot || "";
              const elig = lv.role_id ? eligibility[`${lv.role_id}|${activeBranch || ""}`] : null;
              return (
              <div key={idx} className="border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="font-bold text-[#F97316] text-sm uppercase tracking-wider flex items-center gap-2 flex-wrap">
                    L{lv.level}
                    {idx === (matrix.levels.length - 1) && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5">TOP</span>}
                    <span className="inline-flex border border-slate-300 overflow-hidden">
                      <button
                        data-testid={`level-type-user-${idx}`}
                        onClick={() => isRole && setLevelType(idx, "user")}
                        className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${!isRole ? "bg-[#F97316] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                        Users
                      </button>
                      <button
                        data-testid={`level-type-role-${idx}`}
                        onClick={() => !isRole && setLevelType(idx, "role")}
                        className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${isRole ? "bg-[#F97316] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                        Role
                      </button>
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-normal">
                      {isRole
                        ? (roleName ? `· role: ${roleName}` : "· pick a role")
                        : `· ${selectedCount} approver${selectedCount === 1 ? "" : "s"} selected`}
                    </span>
                  </div>
                  <button
                    data-testid={`remove-level-${idx}`}
                    onClick={() => removeLevel(idx)}
                    className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-xs">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
                {isRole ? (
                  <div>
                    <SearchableSelect
                      testid={`role-picker-${idx}`}
                      value={lv.role_id || ""}
                      onChange={(id) => setLevelRole(idx, id)}
                      items={roles.filter(r => r.active !== false)}
                      getLabel={(r) => r.name}
                      getSubLabel={(r) => r.description || ""}
                      getSearchText={(r) => `${r.name || ""} ${r.description || ""}`}
                      placeholder="— Select a role —"
                      searchPlaceholder={`Search ${roles.filter(r => r.active !== false).length} roles…`}
                      emptyText="No roles match"
                      allowClear={false}
                      className="w-full md:w-80"
                    />
                    <div className="text-[11px] text-slate-500 mt-1.5">
                      Any ONE eligible active member of this role can act at this level. Membership is resolved live — adding or removing users from the role takes effect immediately.
                    </div>
                    {lv.role_id && !elig && (
                      <div className="text-[11px] text-slate-400 mt-1">Checking eligible members…</div>
                    )}
                    {lv.role_id && elig && (elig.eligible_count > 0 ? (
                      <div data-testid={`role-eligibility-${idx}`} className="flex items-start gap-1.5 text-[11px] text-emerald-700 mt-1.5">
                        <UserCheck size={12} className="mt-0.5 shrink-0" />
                        <span>
                          <b>{elig.eligible_count}</b> eligible active member{elig.eligible_count === 1 ? "" : "s"}{activeBranch ? " in this branch" : ""}: {(elig.members || []).map(m => m.name || m.email).join(", ")}
                        </span>
                      </div>
                    ) : (
                      <div data-testid={`role-eligibility-${idx}`} className="flex items-start gap-1.5 text-[11px] text-rose-600 font-semibold mt-1.5">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        <span>No eligible active member in this role{activeBranch ? " for this branch" : ""} — submissions routed here will be blocked (fail-closed).</span>
                      </div>
                    ))}
                  </div>
                ) : (
                <>
                <div className="relative mb-2.5">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    data-testid={`user-search-${idx}`}
                    value={userSearch[idx] || ""}
                    onChange={e => setUserSearch(p => ({ ...p, [idx]: e.target.value }))}
                    placeholder={`Search ${users.length} users by name, email or role…`}
                    className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none"
                  />
                  {q && (
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                      {filteredUsers.length}/{users.length}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {filteredUsers.map(u => {
                    const checked = (lv.user_ids || []).includes(u.id);
                    return (
                      <label
                        key={u.id}
                        data-testid={`user-${idx}-${u.id}`}
                        className={`flex items-center gap-2 px-2 py-1.5 text-xs border cursor-pointer ${checked ? "border-[#F97316] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleUserAtLevel(idx, u.id)}
                          className="accent-[#F97316]"
                        />
                        <span className="truncate">
                          <span className="font-semibold text-slate-800">{u.name || u.email}</span>
                          <span className="text-slate-400 ml-1">· {u.role}</span>
                        </span>
                      </label>
                    );
                  })}
                  {filteredUsers.length === 0 && (
                    <div className="col-span-full text-xs text-slate-500 italic text-center py-3">
                      No users match &ldquo;{q}&rdquo;. Clear the search to see all {users.length} users.
                    </div>
                  )}
                </div>
                </>
                )}
              </div>
            );})}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="add-level-btn"
              onClick={addLevel}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-[#F97316] hover:text-[#F97316]">
              <Plus size={12} /> Add Level
            </button>
            {activeBranch && isOverride && (
              <button
                data-testid="revert-to-default-btn"
                onClick={revertToDefault}
                title="Remove this branch override and fall back to the Default matrix"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-rose-300 text-rose-700 hover:bg-rose-50">
                <RotateCcw size={12} /> Revert to Default
              </button>
            )}
            <button
              data-testid="save-matrix-btn"
              onClick={save}
              className="inline-flex items-center justify-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] text-white sm:ml-auto w-full sm:w-auto">
              <Save size={12} /> <span className="truncate">
                Save {MODULE_LABEL[activeModule]}{activeBranch ? ` · ${branches.find(b => b.id === activeBranch)?.branch_code || "branch"}` : " · Default"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
